import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle2, Film, Library } from 'lucide-react';
import AnalysisProgress from './components/reelCreator/AnalysisProgress';
import ChangeRequest from './components/reelCreator/ChangeRequest';
import ClipUpload from './components/reelCreator/ClipUpload';
import FilmingCard from './components/reelCreator/FilmingCard';
import ReelCreating from './components/reelCreator/ReelCreating';
import ReelCreatorHome from './components/reelCreator/ReelCreatorHome';
import ReelCreatorProgress from './components/reelCreator/ReelCreatorProgress';
import ReelReady from './components/reelCreator/ReelReady';
import ReferenceUpload from './components/reelCreator/ReferenceUpload';
import { createReelCreatorApi } from './lib/reelCreatorApi';
import { creatorErrorMessage } from './lib/reelCreatorConfig';

const filmingClips = (project) => (project?.requiredClips || []).filter((clip) => clip.source === 'filming');
const nextClip = (project) => filmingClips(project).find((clip) => clip.status !== 'approved');

const screenForProject = (project) => {
  if (!project || project.status === 'draft' || project.status === 'reference_uploaded') return 'reference';
  if (project.status === 'analysing_reference') return 'analysing';
  if (project.status === 'filming_required' || project.status === 'validating_clip') return 'filming';
  if (['filming_complete', 'edit_queued', 'editing', 'quality_check'].includes(project.status)) return 'creating';
  if (project.status === 'ready') return 'ready';
  return 'reference';
};

const progressFor = (screen, project) => {
  if (screen === 'home' || screen === 'reference' || screen === 'analysing') return 0;
  if (screen === 'filming' || screen === 'briefing') return 1;
  if (['upload', 'checking', 'accepted', 'retake'].includes(screen)) return 2;
  if (screen === 'creating' || screen === 'complete') return 3;
  if (screen === 'ready' || screen === 'change') return 4;
  return project?.status === 'ready' ? 4 : 0;
};

export default function AIReelCreator({ session, dashboardId = 'vasia' }) {
  const api = useMemo(() => createReelCreatorApi({ accessToken: session?.access_token, dashboardId }), [session?.access_token, dashboardId]);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [screen, setScreen] = useState('home');
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null);
  const [error, setError] = useState('');

  const loadProjects = useCallback(async () => {
    setBusy(true);
    setError('');
    try { setProjects(await api.listProjects()); }
    catch (loadError) {
      console.error('reel_creator_load_error', loadError);
      setError(creatorErrorMessage('load'));
    }
    finally { setBusy(false); }
  }, [api]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const createProject = async () => {
    setBusy(true);
    setError('');
    try {
      const created = await api.createReelProject();
      setProject(created);
      setScreen('reference');
    } catch { setError(creatorErrorMessage('load')); }
    finally { setBusy(false); }
  };

  const openProject = (selected) => {
    setProject(selected);
    setValidation(null);
    setError('');
    setScreen(screenForProject(selected));
  };

  const goHome = async () => {
    setProject(null);
    setValidation(null);
    setScreen('home');
    await loadProjects();
  };

  const submitReference = async (source) => {
    setBusy(true);
    setError('');
    try {
      const upload = await api.uploadReference(project.id, source);
      setScreen('analysing');
      const analysed = await api.analyseReference(project.id, upload?.analysisSource || source);
      setProject(analysed);
      setScreen('briefing');
    } catch (submitError) {
      setScreen('reference');
      setError(submitError?.message || creatorErrorMessage('analyse'));
    } finally { setBusy(false); }
  };

  const uploadClip = async (file) => {
    const clip = nextClip(project);
    if (!clip) return;
    setScreen('checking');
    setError('');
    try {
      await api.uploadFilmedClip(project.id, clip.id, file);
      const result = await api.validateClip(project.id, clip.id, file);
      setProject(result.project);
      setValidation(result.validation);
      if (result.validation.outcome === 'retake') {
        setScreen('retake');
        return;
      }
      setScreen('accepted');
      window.setTimeout(async () => {
        if (result.project.status === 'filming_complete') {
          setScreen('complete');
          window.setTimeout(async () => {
            try {
              const editing = await api.startEdit(project.id);
              setProject(editing);
              setScreen('creating');
            } catch {
              setError(creatorErrorMessage('edit'));
            }
          }, 850);
        } else {
          setScreen('filming');
        }
      }, 850);
    } catch {
      setScreen('upload');
      setError(creatorErrorMessage('upload'));
    }
  };

  useEffect(() => {
    if (screen !== 'creating' || !project?.id) return undefined;
    let cancelled = false;
    let timeoutId;
    const poll = async () => {
      try {
        let updated = project;
        if (project.status === 'filming_complete') updated = await api.startEdit(project.id);
        else updated = await api.getEditStatus(project.id);
        if (cancelled) return;
        setProject(updated);
        if (updated.status === 'ready') {
          setScreen('ready');
          return;
        }
        if (updated.status === 'failed') {
          setError(creatorErrorMessage('edit'));
          return;
        }
        timeoutId = window.setTimeout(poll, 900);
      } catch {
        if (!cancelled) setError(creatorErrorMessage('edit'));
      }
    };
    timeoutId = window.setTimeout(poll, 350);
    return () => { cancelled = true; window.clearTimeout(timeoutId); };
  }, [screen, project?.id, project?.status, api]);

  const requestChange = async (changeRequest) => {
    setBusy(true);
    setError('');
    try {
      const editing = await api.requestChange(project.id, changeRequest);
      setProject(editing);
      setScreen('creating');
    } catch { setError(creatorErrorMessage('edit')); }
    finally { setBusy(false); }
  };

  const clip = nextClip(project);
  const allFilmingClips = filmingClips(project);
  const clipIndex = Math.max(0, allFilmingClips.findIndex((item) => item.id === clip?.id));
  const libraryMatches = (project?.requiredClips || []).filter((item) => item.source === 'library').length;
  const showProgress = screen !== 'home';

  return (
    <section className="reel-creator mx-auto max-w-6xl">
      <header className="mb-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-purple-300">Create faster</p>
            <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">AI Reel Creator</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-400 sm:text-base">Turn a reference Reel into simple filming instructions and a finished edit.</p>
          </div>
          {screen !== 'home' && <button onClick={goHome} className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl border border-gray-700 px-3 text-sm text-gray-300 hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-300"><ArrowLeft size={17} /> <span className="hidden sm:inline">Your Reels</span></button>}
        </div>
        {showProgress && <div className="mt-7 rounded-2xl border border-gray-800 bg-gray-950/60 p-3 sm:p-4"><ReelCreatorProgress current={progressFor(screen, project)} /></div>}
      </header>

      {error && <div role="alert" className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100">{error}</div>}

      {screen === 'home' && <ReelCreatorHome projects={projects} onNew={createProject} onContinue={openProject} loading={busy} />}
      {screen === 'reference' && <ReferenceUpload onSubmit={submitReference} busy={busy} />}
      {screen === 'analysing' && <AnalysisProgress />}
      {screen === 'briefing' && (
        <div className="mx-auto max-w-lg py-7 text-center">
          <Library className="mx-auto text-green-300" size={52} />
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-green-300">Footage library checked</p>
          <h2 className="mt-2 text-3xl font-bold">You Only Need to Film {project.clipsRequired} {project.clipsRequired === 1 ? 'Clip' : 'Clips'}</h2>
          <p className="mt-4 text-gray-400">{libraryMatches
            ? `We found ${libraryMatches} suitable ${libraryMatches === 1 ? 'clip' : 'clips'} in your footage library and will reuse ${libraryMatches === 1 ? 'it' : 'them'}.`
            : 'No approved library clips matched, so every instruction is based directly on this reference Reel.'}</p>
          <button onClick={() => setScreen('filming')} className="mt-7 min-h-14 w-full rounded-xl bg-purple-600 px-5 py-3 font-bold hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-300">SHOW MY FIRST INSTRUCTION</button>
        </div>
      )}
      {screen === 'filming' && clip && <FilmingCard clip={clip} index={clipIndex} total={allFilmingClips.length} onFilmed={() => setScreen('upload')} />}
      {screen === 'upload' && clip && <ClipUpload clip={clip} onUpload={uploadClip} />}
      {screen === 'checking' && <ClipUpload clip={clip || { title: 'Your clip' }} mode="checking" />}
      {screen === 'accepted' && <ClipUpload clip={clip || { title: 'Your clip' }} mode="accepted" />}
      {screen === 'retake' && clip && <ClipUpload clip={clip} mode="retake" validation={validation} onFilmAgain={() => setScreen('filming')} />}
      {screen === 'complete' && <div className="mx-auto max-w-md py-12 text-center" role="status"><CheckCircle2 className="mx-auto text-green-400" size={60} /><h2 className="mt-5 text-3xl font-bold">Filming Complete</h2><p className="mt-3 text-gray-400">You have everything needed for this Reel.</p><p className="mt-2 text-sm text-purple-300">Editing is starting automatically…</p></div>}
      {screen === 'creating' && <ReelCreating status={project?.status} />}
      {screen === 'ready' && project && <ReelReady project={project} onAnother={createProject} onChange={() => setScreen('change')} />}
      {screen === 'change' && <ChangeRequest onSubmit={requestChange} onCancel={() => setScreen('ready')} busy={busy} />}
    </section>
  );
}
